/* global BigInt */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { ethers } from 'ethers';
import { SUPPORTED_CHAINS } from '../config/chains';
import {
  getRpcUpdateInterval,
  normalizeBlockTime,
} from '../utils/blockTiming';
import { validateRpcForChain } from '../utils/rpcValidator';
import {
  describeRpcError,
  getRpcScheme,
} from '../utils/rpcProtocolPolicy';
import {
  getRpcPoolConnectionType,
  normalizeRpcEndpointPool,
} from '../utils/rpcEndpointPool';
import { backendRpcService } from '../services/backendRpcService';
import {
  TRANSACTION_PREHEAT_MODE_PREPARED,
  readTransactionPreheatSettings,
  transactionPreheatService,
  writeTransactionPreheatSettings,
} from '../utils/transactionPreheat';

const GlobalContext = createContext();

export const useGlobal = () => {
  const context = useContext(GlobalContext);
  if (!context) {
    throw new Error('useGlobal must be used within GlobalProvider');
  }
  return context;
};

const STORAGE_KEYS = {
  SELECTED_CHAIN: 'maiko_selected_chain',
  SELECTED_RPC: 'maiko_selected_rpc',
  WALLETS: 'maiko_wallets',
  SELECTED_DEX: 'maiko_selected_dex',
};

const defaultRpcStatus = {
  connected: false,
  connecting: false,
  blockNumber: null,
  gasPrice: null,
  maxFeePerGas: null,
  maxPriorityFeePerGas: null,
  connectionType: null,
  configuredBlockTime: null,
  observedBlockTime: null,
  updateInterval: null,
  lastUpdate: null,
  url: null,
  urls: [],
  endpoints: [],
  connectedCount: 0,
  chainId: null,
  latencyMs: null,
  warnings: [],
  errorDiagnostic: null,
};

const loadFromStorage = (key, defaultValue) => {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  } catch (error) {
    console.error(`Failed to load ${key}:`, error);
    return defaultValue;
  }
};

const saveToStorage = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.error(`Failed to save ${key}:`, error);
  }
};

const detectConnectionType = (url) => {
  const scheme = getRpcScheme(url);

  if (scheme === 'ws' || scheme === 'wss') {
    return 'ws';
  }
  if (scheme === 'http' || scheme === 'https') {
    return 'http';
  }
  throw new Error('Invalid RPC URL. Use http://, https://, ws://, or wss://');
};

const formatFeeData = (feeData) => {
  const gasPriceValue = feeData?.gasPrice || feeData?.maxFeePerGas || 0n;

  return {
    gasPrice: ethers.formatUnits(gasPriceValue, 'gwei'),
    maxFeePerGas: feeData?.maxFeePerGas
      ? ethers.formatUnits(feeData.maxFeePerGas, 'gwei')
      : null,
    maxPriorityFeePerGas: feeData?.maxPriorityFeePerGas
      ? ethers.formatUnits(feeData.maxPriorityFeePerGas, 'gwei')
      : null,
  };
};

export const buildWalletBackupPayload = ({ wallets = [] }) => ({
  wallets: wallets
    .map((wallet) => ({
      address: wallet.address,
      privateKey: wallet.privateKey,
    }))
    .filter((wallet) => wallet.address && wallet.privateKey),
});

export const GlobalProvider = ({ children }) => {
  const [selectedChain, setSelectedChain] = useState(() =>
    loadFromStorage(STORAGE_KEYS.SELECTED_CHAIN, 'bsc')
  );
  const [selectedRpc, setSelectedRpc] = useState(() =>
    loadFromStorage(STORAGE_KEYS.SELECTED_RPC, null)
  );
  const [rpcStatus, setRpcStatus] = useState(defaultRpcStatus);
  const [provider, setProvider] = useState(null);
  const [wsProvider, setWsProvider] = useState(null);
  const [wallets, setWallets] = useState(() =>
    loadFromStorage(STORAGE_KEYS.WALLETS, [])
  );
  const [selectedDex, setSelectedDex] = useState(() =>
    loadFromStorage(STORAGE_KEYS.SELECTED_DEX, null)
  );
  const [transactionPreheatSettings, setTransactionPreheatSettingsState] = useState(() =>
    readTransactionPreheatSettings()
  );
  const [abortController, setAbortController] = useState(null);
  const [walletBackupRetryNonce, setWalletBackupRetryNonce] = useState(0);

  const httpPollingRef = useRef(null);
  const wsHeartbeatRef = useRef(null);
  const providerRef = useRef(null);
  const wsProviderRef = useRef(null);
  const abortControllerRef = useRef(null);
  const selectedRpcUrlRef = useRef(null);
  const selectedRpcUrlsRef = useRef([]);
  const lastBackendBlockRef = useRef(null);
  const walletBackupKeyRef = useRef('');
  const walletBackupInFlightKeyRef = useRef('');
  const walletBackupRetryTimerRef = useRef(null);

  const clearRuntime = useCallback(() => {
    const currentProvider = providerRef.current;
    const currentWsProvider = wsProviderRef.current;

    if (httpPollingRef.current) {
      clearInterval(httpPollingRef.current);
      httpPollingRef.current = null;
    }
    if (wsHeartbeatRef.current) {
      clearInterval(wsHeartbeatRef.current);
      wsHeartbeatRef.current = null;
    }
    if (currentProvider) {
      try {
        currentProvider.removeAllListeners?.();
        currentProvider.destroy?.();
      } catch (error) {
        console.error('Failed to destroy provider:', error);
      }
      providerRef.current = null;
    }
    if (currentWsProvider && currentWsProvider !== currentProvider) {
      try {
        currentWsProvider.removeAllListeners?.();
        currentWsProvider.destroy?.();
      } catch (error) {
        console.error('Failed to destroy WebSocket provider:', error);
      }
    }
    wsProviderRef.current = null;
  }, []);

  const disconnectRpc = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    clearRuntime();
    selectedRpcUrlRef.current = null;
    selectedRpcUrlsRef.current = [];
    lastBackendBlockRef.current = null;
    backendRpcService.disconnectRpc().catch(() => {});
    setProvider(null);
    setWsProvider(null);
    setSelectedRpc(null);
    setRpcStatus(defaultRpcStatus);
    setAbortController(null);
  }, [clearRuntime]);

  const forceDisconnect = useCallback(() => {
    disconnectRpc();
  }, [disconnectRpc]);

  const connectRpc = useCallback(async (rpcUrlsInput, chainBlockTime = 12_000) => {
    const controller = new AbortController();
    const configuredBlockTime = normalizeBlockTime(chainBlockTime);
    const currentChainConfig = SUPPORTED_CHAINS[selectedChain] || {};
    const expectedChainId = currentChainConfig.id || null;
    const requestedRpcUrls = normalizeRpcEndpointPool(rpcUrlsInput);
    const requestedPrimaryUrl = requestedRpcUrls[0] || '';

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    clearRuntime();
    abortControllerRef.current = controller;
    setAbortController(controller);

    try {
      if (requestedRpcUrls.length === 0) {
        throw new Error('At least one RPC URL is required');
      }

      const validations = await Promise.all(requestedRpcUrls.map(async (url) => ({
        url,
        ...await validateRpcForChain(selectedChain, url),
      })));
      const rejected = validations.find((validation) => !validation.ok);
      if (rejected) {
        setRpcStatus(defaultRpcStatus);
        return {
          success: false,
          message: `RPC validation failed (${rejected.url}): ${rejected.reason}`,
        };
      }

      const requestedConnectionType = getRpcPoolConnectionType(requestedRpcUrls);
      const updateInterval = getRpcUpdateInterval({ blockTimeMs: configuredBlockTime });

      setRpcStatus((prev) => ({
        ...prev,
        connecting: true,
        connected: false,
        configuredBlockTime,
        observedBlockTime: configuredBlockTime,
        updateInterval,
        url: requestedPrimaryUrl,
        urls: requestedRpcUrls,
        endpoints: [],
        connectedCount: 0,
        chainId: null,
        latencyMs: null,
        warnings: [],
        errorDiagnostic: null,
      }));

      const initialData = await backendRpcService.connectRpc({
        chainKey: selectedChain,
        chainId: expectedChainId,
        chainName: currentChainConfig.name || selectedChain,
        rpcUrl: requestedPrimaryUrl,
        rpcUrls: requestedRpcUrls,
        blockTimeMs: configuredBlockTime,
      });

      if (controller.signal.aborted) {
        throw new Error('RPC connection was cancelled');
      }

      const connectedChainId = Number(initialData?.chainId || 0) || null;
      const connectedPrimaryUrl = initialData?.url || requestedPrimaryUrl;
      const connectedRpcUrls = normalizeRpcEndpointPool(
        initialData?.rpcUrls?.length ? initialData.rpcUrls : requestedRpcUrls
      );
      const connectedEndpoints = Array.isArray(initialData?.endpoints)
        ? initialData.endpoints
        : [];
      const hasConnectedWebSocket = connectedEndpoints.length > 0
        ? connectedEndpoints.some((endpoint) => (
          endpoint?.connected !== false && endpoint?.connectionType === 'ws'
        ))
        : connectedRpcUrls.some((url) => /^wss?:\/\//i.test(url));
      const connectionType = initialData?.connectionType
        || (connectedPrimaryUrl ? detectConnectionType(connectedPrimaryUrl) : requestedConnectionType);
      if (expectedChainId && connectedChainId && connectedChainId !== expectedChainId) {
        clearRuntime();
        abortControllerRef.current = null;
        setAbortController(null);
        setProvider(null);
        setWsProvider(null);
        setRpcStatus({
          ...defaultRpcStatus,
          url: connectedPrimaryUrl,
          urls: connectedRpcUrls,
          endpoints: connectedEndpoints,
          chainId: connectedChainId,
          errorDiagnostic: `RPC chain mismatch: expected ${expectedChainId}, got ${connectedChainId}`,
        });
        return {
          success: false,
          message: `RPC chain mismatch: expected ${expectedChainId}, got ${connectedChainId}`,
          chainId: connectedChainId,
        };
      }

      const newProvider = backendRpcService.createProvider({
        chainKey: selectedChain,
        chainId: connectedChainId || expectedChainId,
        chainName: currentChainConfig.name || selectedChain,
        rpcUrl: connectedPrimaryUrl,
        blockTimeMs: configuredBlockTime,
      });

      providerRef.current = newProvider;
      selectedRpcUrlRef.current = connectedPrimaryUrl;
      selectedRpcUrlsRef.current = connectedRpcUrls;
      setProvider(newProvider);
      if (hasConnectedWebSocket) {
        wsProviderRef.current = newProvider;
        setWsProvider(newProvider);
      } else {
        setWsProvider(null);
      }

      const feeStatus = formatFeeData({
        gasPrice: initialData?.gasPrice ? BigInt(initialData.gasPrice) : null,
        maxFeePerGas: initialData?.maxFeePerGas ? BigInt(initialData.maxFeePerGas) : null,
        maxPriorityFeePerGas: initialData?.maxPriorityFeePerGas ? BigInt(initialData.maxPriorityFeePerGas) : null,
      });
      const blockNumber = Number(initialData?.blockNumber ?? 0) || null;
      lastBackendBlockRef.current = blockNumber;

      setRpcStatus({
        connected: true,
        connecting: false,
        blockNumber,
        ...feeStatus,
        connectionType,
        configuredBlockTime,
        observedBlockTime: configuredBlockTime,
        updateInterval,
        lastUpdate: initialData?.lastUpdate || new Date().toISOString(),
        url: connectedPrimaryUrl,
        urls: connectedRpcUrls,
        endpoints: connectedEndpoints,
        connectedCount: Number(initialData?.connectedCount || connectedEndpoints.filter(
          (endpoint) => endpoint?.connected !== false
        ).length || 1),
        chainId: connectedChainId || expectedChainId,
        latencyMs: initialData?.latencyMs ?? null,
        warnings: [],
        errorDiagnostic: null,
      });

      setSelectedRpc({
        url: connectedPrimaryUrl,
        urls: connectedRpcUrls,
        type: connectionType,
      });

      return {
        success: true,
        message: 'RPC connected',
        connectionType,
        observedBlockTime: configuredBlockTime,
        updateInterval,
        latencyMs: initialData?.latencyMs ?? null,
        chainId: connectedChainId || expectedChainId,
        url: connectedPrimaryUrl,
        urls: connectedRpcUrls,
        endpoints: connectedEndpoints,
        connectedCount: Number(initialData?.connectedCount || connectedRpcUrls.length),
        warnings: [],
      };
    } catch (error) {
      console.error('RPC connection failed:', error);
      const diagnostic = describeRpcError(error);

      if (controller.signal.aborted) {
        return { success: false, message: 'RPC connection was cancelled' };
      }

      clearRuntime();
      abortControllerRef.current = null;
      selectedRpcUrlRef.current = null;
      selectedRpcUrlsRef.current = [];
      lastBackendBlockRef.current = null;
      setAbortController(null);
      setProvider(null);
      setWsProvider(null);
      setRpcStatus({
        ...defaultRpcStatus,
        url: requestedPrimaryUrl,
        urls: requestedRpcUrls,
        errorDiagnostic: diagnostic,
      });

      return { success: false, message: diagnostic };
    }
  }, [clearRuntime, selectedChain]);

  const addWallet = useCallback((wallet) => {
    setWallets((prev) => [...prev, {
      ...wallet,
      id: Date.now() + Math.random(),
      enabled: wallet.enabled !== undefined ? wallet.enabled : true,
      createdAt: new Date().toISOString(),
    }]);
  }, []);

  const addWalletsBatch = useCallback((walletList) => {
    const newWallets = walletList.map((wallet) => ({
      ...wallet,
      id: Date.now() + Math.random(),
      enabled: wallet.enabled !== undefined ? wallet.enabled : true,
      createdAt: new Date().toISOString(),
    }));
    setWallets((prev) => [...prev, ...newWallets]);
  }, []);

  const removeWallet = useCallback((id) => {
    setWallets((prev) => prev.filter((wallet) => wallet.id !== id));
  }, []);

  const toggleWallet = useCallback((id) => {
    setWallets((prev) => prev.map((wallet) =>
      wallet.id === id ? { ...wallet, enabled: !wallet.enabled } : wallet
    ));
  }, []);

  const enableAllWallets = useCallback(() => {
    setWallets((prev) => prev.map((wallet) => ({ ...wallet, enabled: true })));
  }, []);

  const disableAllWallets = useCallback(() => {
    setWallets((prev) => prev.map((wallet) => ({ ...wallet, enabled: false })));
  }, []);

  const clearAllWallets = useCallback(() => {
    if (window.confirm('Are you sure you want to clear all wallets?')) {
      setWallets([]);
    }
  }, []);

  const getEnabledWallets = useCallback(() => wallets.filter((wallet) => wallet.enabled), [wallets]);

  const setTransactionPreheatSettings = useCallback((nextSettings) => {
    setTransactionPreheatSettingsState((prev) => {
      const resolved = typeof nextSettings === 'function' ? nextSettings(prev) : nextSettings;
      const normalized = writeTransactionPreheatSettings(resolved);
      transactionPreheatService.setSettings(normalized);
      return normalized;
    });
  }, []);

  const scheduleWalletBackupRetry = useCallback(() => {
    if (walletBackupRetryTimerRef.current) {
      clearTimeout(walletBackupRetryTimerRef.current);
    }
    walletBackupRetryTimerRef.current = setTimeout(() => {
      walletBackupRetryTimerRef.current = null;
      setWalletBackupRetryNonce((nonce) => nonce + 1);
    }, 3000);
  }, []);

  useEffect(() => {
    saveToStorage(STORAGE_KEYS.SELECTED_CHAIN, selectedChain);
  }, [selectedChain]);

  useEffect(() => {
    if (selectedRpc) {
      saveToStorage(STORAGE_KEYS.SELECTED_RPC, selectedRpc);
    } else {
      localStorage.removeItem(STORAGE_KEYS.SELECTED_RPC);
    }
  }, [selectedRpc]);

  useEffect(() => {
    saveToStorage(STORAGE_KEYS.WALLETS, wallets);
  }, [wallets]);

  useEffect(() => {
    const payload = buildWalletBackupPayload({ wallets });
    if (payload.wallets.length === 0) {
      walletBackupKeyRef.current = '';
      walletBackupInFlightKeyRef.current = '';
      return;
    }

    const backupKey = JSON.stringify(payload.wallets.map((wallet) => [
      wallet.address,
      wallet.privateKey,
    ]));
    if (walletBackupKeyRef.current === backupKey) return;
    if (walletBackupInFlightKeyRef.current === backupKey) return;
    walletBackupInFlightKeyRef.current = backupKey;

    let cancelled = false;
    backendRpcService.backupWallets(payload)
      .then(() => {
        if (cancelled) return;
        walletBackupKeyRef.current = backupKey;
      })
      .catch((error) => {
        if (cancelled) return;
        console.warn('Wallet.json backup failed; retrying soon:', error?.message || error);
        scheduleWalletBackupRetry();
      })
      .finally(() => {
        if (walletBackupInFlightKeyRef.current === backupKey) {
          walletBackupInFlightKeyRef.current = '';
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    wallets,
    walletBackupRetryNonce,
    scheduleWalletBackupRetry,
  ]);

  useEffect(() => {
    saveToStorage(STORAGE_KEYS.SELECTED_DEX, selectedDex);
  }, [selectedDex]);

  useEffect(() => {
    const normalized = writeTransactionPreheatSettings(transactionPreheatSettings);
    transactionPreheatService.setSettings(normalized);

    if (
      normalized.preheatMode !== TRANSACTION_PREHEAT_MODE_PREPARED
      || !rpcStatus.connected
      || !provider
    ) {
      transactionPreheatService.clearNonces();
      transactionPreheatService.clearFeeData();
      return undefined;
    }

    let cancelled = false;
    const preheat = async () => {
      try {
        const enabledWallets = wallets.filter((wallet) => wallet.enabled !== false);
        await Promise.all([
          transactionPreheatService.preheatWalletNonces({ wallets: enabledWallets, provider }),
          provider.getFeeData()
            .then((feeData) => transactionPreheatService.primeFeeData(feeData))
            .catch(() => {}),
        ]);
      } catch (error) {
        if (!cancelled) {
          console.debug('Transaction preheat skipped:', error);
        }
      }
    };

    preheat();
    const timer = setInterval(preheat, 5000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [provider, rpcStatus.connected, transactionPreheatSettings, wallets]);

  useEffect(() => {
    const unsubscribe = backendRpcService.onRpcStatus((status) => {
      if (!status) return;
      const statusUrl = status.url || selectedRpcUrlRef.current;
      const selectedUrls = selectedRpcUrlsRef.current;
      if (selectedUrls.length > 0 && statusUrl && !selectedUrls.includes(statusUrl)) {
        return;
      }
      if (statusUrl) selectedRpcUrlRef.current = statusUrl;

      const blockNumber = Number(status.blockNumber ?? 0) || null;
      const feeStatus = formatFeeData({
        gasPrice: status.gasPrice ? BigInt(status.gasPrice) : null,
        maxFeePerGas: status.maxFeePerGas ? BigInt(status.maxFeePerGas) : null,
        maxPriorityFeePerGas: status.maxPriorityFeePerGas ? BigInt(status.maxPriorityFeePerGas) : null,
      });

      if (blockNumber && blockNumber !== lastBackendBlockRef.current) {
        lastBackendBlockRef.current = blockNumber;
        providerRef.current?.emit?.('block', blockNumber);
        if (wsProviderRef.current && wsProviderRef.current !== providerRef.current) {
          wsProviderRef.current.emit?.('block', blockNumber);
        }
      }

      setRpcStatus((prev) => ({
        ...prev,
        connected: status.connected !== false,
        connecting: false,
        blockNumber: blockNumber || prev.blockNumber,
        ...feeStatus,
        chainId: Number(status.chainId || prev.chainId || 0) || prev.chainId,
        latencyMs: status.latencyMs ?? prev.latencyMs,
        connectionType: status.connectionType || prev.connectionType,
        url: statusUrl || prev.url,
        urls: normalizeRpcEndpointPool(status.rpcUrls?.length ? status.rpcUrls : prev.urls),
        endpoints: Array.isArray(status.endpoints) ? status.endpoints : prev.endpoints,
        connectedCount: Number(status.connectedCount ?? prev.connectedCount ?? 0),
        lastUpdate: status.lastUpdate || new Date().toISOString(),
        errorDiagnostic: status.connected === false
          ? (status.error || 'RPC disconnected')
          : null,
      }));
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (selectedRpc && !rpcStatus.connected && !rpcStatus.connecting) {
        const rpcUrls = typeof selectedRpc === 'string'
          ? [selectedRpc]
          : (selectedRpc.urls?.length ? selectedRpc.urls : [selectedRpc.url]);
        const blockTime = SUPPORTED_CHAINS[selectedChain]?.blockTime || 12_000;
        connectRpc(rpcUrls, blockTime);
      }
    }, 800);

    return () => clearTimeout(timer);
  }, [connectRpc, rpcStatus.connected, rpcStatus.connecting, selectedChain, selectedRpc]);

  useEffect(() => () => {
    clearRuntime();
    if (walletBackupRetryTimerRef.current) {
      clearTimeout(walletBackupRetryTimerRef.current);
      walletBackupRetryTimerRef.current = null;
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  }, [clearRuntime]);

  const value = {
    selectedChain,
    setSelectedChain,
    selectedRpc,
    rpcStatus,
    provider,
    wsProvider,
    connectRpc,
    disconnectRpc,
    forceDisconnect,
    abortController,
    wallets,
    addWallet,
    addWalletsBatch,
    removeWallet,
    toggleWallet,
    enableAllWallets,
    disableAllWallets,
    clearAllWallets,
    getEnabledWallets,
    selectedDex,
    setSelectedDex,
    transactionPreheatSettings,
    setTransactionPreheatSettings,
  };

  return (
    <GlobalContext.Provider value={value}>
      {children}
    </GlobalContext.Provider>
  );
};
